/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable prefer-const */
import { CommentOutlined, DownOutlined, ExclamationCircleOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Menu, Modal, Radio, Space, Table, TableColumnsType, TableProps, Tag, Tooltip } from 'antd';
import { isBefore, isToday, parseISO } from 'date-fns';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useGlobalContext } from '../../../context/GlobalContext';
import { IAcaoEmLoteDTO, IDespesaFullDTO } from '../../../models/happy-code-api.model';
import { PageableResponse } from '../../../models/response.model';
import { useDespesaService } from '../../../services/despesa.service';
import { useNotificationService } from '../../../services/notification.service';
import useEmitirReciboHook from '../hooks/use-emitir-recibo-hook';
import Styles from './components.module.scss';
import ModalAlterarConta from './modal-change-account';
import ModalAlteraDataVencimento from './modal-change-date';
import ModalAlterarValor from './modal-change-value';
import ModalConfirmarPagamento from './modal-confim-payment';
import EditParcelModal from './modal-edit';
import ModalEdicaoLote from './modal-bath-edit';

interface TabelaContasProps {
	data: PageableResponse<IDespesaFullDTO>;
	loading: boolean;
	buildFiltersAndFetch: (e?: React.FormEvent, page?: number) => void;
	page: number;
}

export default function TabelaContas({ data, loading, buildFiltersAndFetch, page }: TabelaContasProps) {
	const [selectedRowKeys, setSelectedRowKeys] = useState<number[] | React.Key[]>([]);
	const [selectedRows, setSelectedRows] = useState<IDespesaFullDTO[]>([]);
	const despesaService = useDespesaService();
	const notification = useNotificationService();
	const history = useHistory();

	const { setIsGlobalLoading } = useGlobalContext();

	const [openPagamentoModal, setOpenPagamentoModal] = useState(false);
	const [openAlterarValorModal, setOpenAlterarValorModal] = useState(false);
	const [openAlterarVencimentoModal, setOpenAlterarVencimentoModal] = useState(false);
	const [openAlterarConta, setOpenAlterarContaModal] = useState(false);
	const [openEdicaoEmLote, setOpenEdicaoEmLoteModal] = useState(false);
	const [despesaSelecionada, setDespesaSelecionada] = useState<IDespesaFullDTO>();
	const [openFastEdit, setOpenFastEdit] = useState(false);
	const [acaoEmLote, setAcaoEmLote] = useState<IAcaoEmLoteDTO>({
		idsDespesas: [],
		acao: 'EXCLUIR_TODAS',
		vlAjuste: 0,
		dataAjuste: new Date(),
		idConta: 0,
	});

	const { gerarRecibo } = useEmitirReciboHook();

	const hasAnyPaidSelected = selectedRows.some((r) => r.flPago);
	const noSelection = selectedRows.length === 0;

	const rowSelection: TableProps<IDespesaFullDTO>['rowSelection'] = {
		selectedRowKeys,
		onChange: (keys, rows) => {
			setSelectedRowKeys(keys);
			setSelectedRows(rows);
		},
	};

	const updateTable = (pageTable = page) => {
		buildFiltersAndFetch(undefined, pageTable - 1);
	};

	const cleanSelection = () => {
		setSelectedRowKeys([]);
		setSelectedRows([]);
	};
	const showDeleteConfirm = (despesa: IDespesaFullDTO) => {
		let agreed = false;
		const modal = Modal.confirm({
			title: `Deseja realmente excluir a despesa?`,
			icon: <ExclamationCircleOutlined rev={undefined} style={{ color: 'orange' }} />,
			content: (
				<div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
					<p>Despesa: {despesa?.dsDespesa}</p>
					<Checkbox
						onChange={(e) => {
							agreed = e.target.checked;
							modal.update({
								okButtonProps: {
									disabled: !agreed,
								},
							});
						}}
					>
						Confirmo a exclusão da despesa e estou ciente de que essa ação não poderá ser desfeita.
					</Checkbox>
				</div>
			),
			okText: 'Sim',
			okType: 'danger',
			cancelText: 'Não',
			okButtonProps: {
				disabled: true,
			},
			onOk: () =>
				despesaService
					.patch({ id: despesa?.id, ativo: false })
					.then(() => {
						notification({
							description: 'Despesa excluída com sucesso!',
							type: 'success',
							message: 'Sucesso!',
						});
					})
					.finally(() => {
						cleanSelection();
						setIsGlobalLoading(false);
						updateTable();
					}),
			onCancel: () => {},
		});
	};

	const showDeleteWithOptions = (despesa: IDespesaFullDTO) => {
		let escolha: 'ONE' | 'NOT_OVERDUE' | 'OPEN' | 'ALL' = 'ONE';
		Modal.confirm({
			title: 'Excluir lançamento',
			icon: <ExclamationCircleOutlined rev={undefined} style={{ color: 'orange' }} />,
			width: 600,
			content: (
				<div style={{ padding: '8px 0' }}>
					<p style={{ marginBottom: '18px' }}>Ao confirmar esta ação, quais lançamentos você deseja excluir?</p>
					<Radio.Group
						defaultValue="ONE"
						onChange={(e) => {
							escolha = e.target.value;
						}}
						style={{ width: '100%' }}
					>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<Radio value="ONE">Somente o lançamento atual</Radio>

							<Radio value="NOT_OVERDUE">
								Lançamento atual e todos os <strong>não vencidos</strong> que não possuam baixas
							</Radio>

							<Radio value="OPEN">
								Lançamento atual e todos os <strong>em aberto</strong> (vencidos + a vencer) que não possuam baixas
							</Radio>

							<Radio value="ALL">Todos os lançamentos</Radio>
						</Space>
					</Radio.Group>
				</div>
			),
			okText: 'Confirmar',
			okType: 'danger',
			cancelText: 'Cancelar',
			onOk: async () => {
				setIsGlobalLoading(true);
				try {
					switch (escolha) {
						case 'ONE':
							await despesaService.patch({ id: despesa.id, ativo: false });
							break;
						case 'NOT_OVERDUE':
							await despesaService.deleteParcelasNaoVencidas(despesa.id);
							break;
						case 'OPEN':
							await despesaService.deleteParcelasEmAberto(despesa.id);
							break;
						case 'ALL':
							await despesaService.acaoEmLote({
								acao: 'EXCLUIR_TODAS',
								idsDespesas: [despesa.id],
							});
							break;
					}
					notification({ message: 'Sucesso!', description: 'Lançamentos excluídos.', type: 'success' });
					updateTable();
				} finally {
					setIsGlobalLoading(false);
				}
			},
		});
	};

	const handleSingleOption = (value: string, despesa?: IDespesaFullDTO) => {
		setDespesaSelecionada(despesa);
		if (value === 'GERAR_RECIBO' && despesa?.flPago) {
			gerarRecibo(despesa);
		}
		if (value === 'MARCAR_COMO_PAGO') setOpenPagamentoModal(true);
		if (value === 'EDITAR_RAPIDO') setOpenFastEdit(true);
		if (value === 'EXCLUIR' && despesa) {
			if (despesa.planoFinanceiro?.id) {
				showDeleteWithOptions(despesa);
			} else {
				showDeleteConfirm(despesa);
			}
		}
		if (value === 'VISUALIZAR') history.push(`/contas-a-pagar/cadastro-despesas/${despesa?.id}`);
	};

	const handleActionMultiOption = (value: any) => {
		const modaisAbertura = ['OPEN-MODAL-VALOR', 'OPEN-MODAL-VENCIMENTO', 'OPEN-MODAL-CONTA', 'OPEN-MODAL-EDICAO-LOTE'];
		if (modaisAbertura.includes(value)) {
			switch (value) {
				case 'OPEN-MODAL-VALOR':
					setOpenAlterarValorModal(true);
					break;
				case 'OPEN-MODAL-VENCIMENTO':
					setOpenAlterarVencimentoModal(true);
					break;
				case 'OPEN-MODAL-CONTA':
					setOpenAlterarContaModal(true);
					break;
				case 'OPEN-MODAL-EDICAO-LOTE':
					setOpenEdicaoEmLoteModal(true);
					break;
			}
			return;
		}
		acaoEmLote.acao = value;
		acaoEmLote.idsDespesas = selectedRowKeys as number[];
		if (acaoEmLote.idsDespesas.length === 0) {
			notification({
				description: 'Selecione pelo menos uma despesa.',
				type: 'warning',
				message: 'Atenção!',
			});
			return;
		}
		setIsGlobalLoading(true);
		despesaService
			.acaoEmLote(acaoEmLote)
			.then(() => {
				notification({
					description: `Despesas ${acaoEmLote.acao === 'EXCLUIR_TODAS' ? 'excluidas' : 'alteradas'} com sucesso!`,
					type: 'success',
					message: 'Sucesso',
				});
			})
			.finally(() => {
				cleanSelection();
				updateTable();
				setIsGlobalLoading(false);
				setSelectedRowKeys([]);
			});
	};
	const menuLote = (
		<Menu onClick={({ key }) => handleActionMultiOption(key)}>
			<Menu.Item disabled={noSelection} key="EXCLUIR_TODAS">
				Excluir
			</Menu.Item>
			<Menu.Item disabled={hasAnyPaidSelected || noSelection} key="MARCAR_TODAS_COMO_PAGAS">
				Marcar como pago
			</Menu.Item>
			<Menu.Item disabled={noSelection} key="MARCAR_TODAS_COMO_EM_ABERTO">
				Alterar para em aberto
			</Menu.Item>
			<Menu.Item disabled={hasAnyPaidSelected || noSelection} key={'OPEN-MODAL-VALOR'}>
				Alterar valor
			</Menu.Item>
			<Menu.Item disabled={hasAnyPaidSelected || noSelection} key={'OPEN-MODAL-VENCIMENTO'}>
				Alterar vencimento
			</Menu.Item>
			<Menu.Item disabled={hasAnyPaidSelected || noSelection} key={'OPEN-MODAL-CONTA'}>
				Alterar conta
			</Menu.Item>
			<Menu.Item disabled={hasAnyPaidSelected || noSelection} key={'OPEN-MODAL-EDICAO-LOTE'}>
				Editar
			</Menu.Item>
		</Menu>
	);
	const verificarMenu = (despesa: IDespesaFullDTO) => {
		if (despesa.flPago) {
			return (
				<Menu onClick={({ key }) => handleSingleOption(key, despesa)}>
					<Menu.Item key="VISUALIZAR">Visualizar / Editar</Menu.Item>
					<Menu.Item key="EXCLUIR">Excluir</Menu.Item>
					<Menu.Item key="GERAR_RECIBO">Emitir Recibo</Menu.Item>
				</Menu>
			);
		} else {
			return (
				<Menu onClick={({ key }) => handleSingleOption(key, despesa)}>
					<Menu.Item key="MARCAR_COMO_PAGO">Alterar para pago</Menu.Item>
					<Menu.Item key="VISUALIZAR">Visualizar / Editar</Menu.Item>
					<Menu.Item key="EDITAR_RAPIDO">Edição rápida</Menu.Item>
					<Menu.Item key="EXCLUIR">Excluir</Menu.Item>
				</Menu>
			);
		}
	};

	const columns: TableColumnsType<IDespesaFullDTO> = [
		{
			title: 'ID',
			key: 'id',
			dataIndex: 'id',
		},
		{
			title: 'Vencimento',
			key: 'vencimento',
			dataIndex: 'dhVencimento',
			render: (value: string) => (value ? new Date(value).toLocaleDateString('pt-BR') : (
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				-
			</div>
			)),
		},
		{
			title: 'Pagamento',
			key: 'pagamento',
			dataIndex: 'dhPagamento',
			render: (value: string) =>
				value ? new Date(value).toLocaleDateString('pt-BR') : <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>-</div>,
		},
		{
			title: 'Descrição',
			key: 'descricao',
			dataIndex: 'dsDespesa',
			render: (value: string) => value.slice(0, 60).concat(value.length > 60 ? '...' : ''),
		},
		{
			title: 'Valor (R$)',
			key: 'valor',
			dataIndex: 'vlDespesa',
			render: (value: number) => value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
		},
		{
			title: 'Valor pago',
			key: 'vlPago',
			dataIndex: 'vlPago',
			render: (value: number) => value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
		},
		{
			title: 'Categoria',
			key: 'categoria',
			dataIndex: ['categoria', 'dsCategoria'],
		},
		{
			title: 'Status',
			key: 'status',
			dataIndex: ['statusDespesa', 'dsStatusDespesa'],
			render: (_: any, despesa: IDespesaFullDTO) => {
				const vencimento = parseISO(new Date(despesa?.dhVencimento).toISOString());
				const hoje = new Date();
				const isPaga = despesa.flPago;

				let statusLabel = 'Em Aberto';
				let color = 'blue';

				if (!isPaga && isBefore(vencimento, hoje) && !isToday(vencimento)) {
					statusLabel = 'Vencida';
					color = 'red';
				} else if (isPaga) {
					statusLabel = 'Paga';
					color = 'green';
				}

				return (
					<Tag style={{ borderRadius: '10px' }} color={color}>
						{statusLabel}
					</Tag>
				);
			},
		},
		{
			title: 'Anexos',
			key: 'anexos',
			render: (despesa: IDespesaFullDTO) => {
				if (despesa.hasArquivosDespesa) {
					return (
						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
							<PaperClipOutlined style={{ color: '#6A7C96', fontSize: '20px' }} rev={undefined} />
						</div>
					);
				} else {
					return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>-</div>;
				}
			},
		},
		{
			title: 'Observações',
			key: 'observacoes',
			render: (despesa: IDespesaFullDTO) => {
				if (despesa?.observacao) {
					return (
						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
							<Tooltip title={despesa?.observacao?.slice(0, 45).concat(despesa?.observacao?.length > 50 ? '...' : '')}>
								<CommentOutlined style={{ color: '#6A7C96', fontSize: '20px' }} rev={undefined} />
							</Tooltip>
						</div>
					);
				} else {
					return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>-</div>;
				}
			},
		},
		{
			title: 'Ultima alteração',
			key: 'ultimoUsuarioAlteracao',
			dataIndex: 'ultimoUsuarioAlteracao',
			render: (value: string) => (value ? value : '-'),
		},
		{
			title: 'Ações',
			key: 'acoes',
			render: (_: any, record: IDespesaFullDTO) => {
				const menu = verificarMenu(record);
				return (
					<Dropdown overlay={menu} trigger={['click']}>
						<Button style={{ borderRadius: 16, fontWeight: 600 }}>
							Ações <DownOutlined rev={undefined} />
						</Button>
					</Dropdown>
				);
			},
		},
	];
	return (
		<div className={Styles.tableWrapper} id="table-wrapper-despesas">
			<div className={Styles.tableHeader}>
				<span>{selectedRowKeys.length} registro(s) selecionado(s)</span>
				<Dropdown overlay={menuLote} trigger={['hover']}>
					<Button style={{ borderRadius: 16, fontWeight: 600 }}>
						Ações em lote <DownOutlined rev={undefined} />
					</Button>
				</Dropdown>
			</div>
			<Table<IDespesaFullDTO>
				rowKey={'id'}
				size="small"
				loading={loading}
				pagination={{
					showSizeChanger: false,
					current: page,
					total: data?.totalElements,
					onChange: (page) => updateTable(page),
				}}
				rowSelection={rowSelection}
				columns={columns}
				dataSource={data?.content}
				title={() => <strong>Despesas</strong>}
				onRow={(record) => ({
					onClick: () => {
						const alreadySelected = selectedRowKeys.includes(record.id);
						const newSelectedRowKeys = alreadySelected ? selectedRowKeys.filter((key) => key !== record.id) : [...selectedRowKeys, record.id];

						const newSelectedRows = alreadySelected ? selectedRows.filter((row) => row.id !== record.id) : [...selectedRows, record];

						setSelectedRowKeys(newSelectedRowKeys);
						setSelectedRows(newSelectedRows);
					},
				})}
			/>
			<div style={{ textAlign: 'center' }}>
				<small style={{ color: '#6A7C96', fontSize: '14px', fontWeight: 'bold' }}> Foram encontrados {data?.totalElements ?? 0} registros</small>
			</div>
			<ModalConfirmarPagamento open={openPagamentoModal} setOpen={setOpenPagamentoModal} despesa={despesaSelecionada} refreshTable={() => updateTable()} />
			<EditParcelModal open={openFastEdit} setOpen={setOpenFastEdit} despesa={despesaSelecionada} refreshTable={() => updateTable()} />
			<ModalAlterarValor
				open={openAlterarValorModal}
				setOpen={setOpenAlterarValorModal}
				setAcaoEmLote={setAcaoEmLote}
				handleAction={handleActionMultiOption}
				acaoEmLote={acaoEmLote}
			/>
			<ModalAlteraDataVencimento
				open={openAlterarVencimentoModal}
				setOpen={setOpenAlterarVencimentoModal}
				setAcaoEmLote={setAcaoEmLote}
				handleAction={handleActionMultiOption}
				acaoEmLote={acaoEmLote}
			/>
			<ModalAlterarConta
				open={openAlterarConta}
				setOpen={setOpenAlterarContaModal}
				setAcaoEmLote={setAcaoEmLote}
				handleAction={handleActionMultiOption}
				acaoEmLote={acaoEmLote}
			/>
			<ModalEdicaoLote
				refreshTable={() => updateTable()}
				open={openEdicaoEmLote}
				setOpen={setOpenEdicaoEmLoteModal}
				idsDespesas={selectedRowKeys}
				setSelectedRowKeys={setSelectedRowKeys}
			/>
		</div>
	);
}
